import {AnalysisInfo} from '@domain/rcfa'
import {StoppageInfo} from '@domain/stoppages'
import {AnalysisType} from '@hconnect/common/types'
import {PageTitle, PageContainer, TextOnBackground} from '@hconnect/uikit/src/lib2'
import {Box, Card} from '@mui/material'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useLocation, useNavigate, useSearchParams} from 'react-router'

import {useHasRcfaAdminRole} from '../../hooks/useHasRcfaAdminRole'
import {FEATURE_NAMESPACE} from '../../localization'
import {getPersistedSearchParams} from '../../utils'
import {ManageContributors} from '../common/ManageContributors'

import {NewAnalysisStep} from './analysisCreate.types'
import {CreateAnalysisForm, NavigateToPlantOptions} from './CreateAnalysisForm'
import {CreateAnalysisSteps} from './CreateAnalysisSteps'
import {RcfaCreatePlantOption} from './useRcfaCreatePlantOptions'

export interface CreateAnalysisPageProperties {
  onGoToStoppageList?: () => Promise<void> | void
  onNavigateToPlant?: (
    plant: RcfaCreatePlantOption,
    options: NavigateToPlantOptions
  ) => Promise<void> | void
}

export const CreateAnalysisPage = ({
  onGoToStoppageList,
  onNavigateToPlant
}: CreateAnalysisPageProperties) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [step, setStep] = useState<NewAnalysisStep>(NewAnalysisStep.fillAnalysisForm)
  const [analysis, setAnalysis] = useState<AnalysisInfo | undefined>()
  const canReassign = useHasRcfaAdminRole()

  const type = searchParams.get('type')
  const selectedStoppage: StoppageInfo | undefined = location?.state?.selectedStoppage

  if (!selectedStoppage && type === 'stoppage') {
    throw new Error('Stoppage is missing in location state')
  }

  const onAnalysisCreated = (analysis: AnalysisInfo) => {
    setAnalysis(analysis)
  }

  const navigateToDetail = (analysisId: number) =>
    navigate(
      {
        pathname: `../graphDetail/${analysisId}/graph`,
        search: getPersistedSearchParams(searchParams).toString()
      },
      {state: location}
    )

  return (
    <PageContainer
      sx={{
        minWidth: '550px'
      }}
    >
      <PageTitle ml={3} mb={0} className="text-foreground">
        {t('pages.createNewPage')}
      </PageTitle>
      <TextOnBackground variant="caption" ml={3} contrast="low">
        {t('pages.mainTitle')}
      </TextOnBackground>

      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          flexShrink: 1,
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3
        }}
      >
        <CreateAnalysisSteps step={step} type={type} />
        {step === NewAnalysisStep.fillAnalysisForm && (
          <CreateAnalysisForm
            onGoBack={() => navigate(-1)}
            onGoNext={() => setStep(NewAnalysisStep.addContributors)}
            onAnalysisCreated={onAnalysisCreated}
            onGoToStoppageList={onGoToStoppageList}
            onNavigateToPlant={onNavigateToPlant}
            canReassign={canReassign}
            selectedStoppage={selectedStoppage}
            analysisType={type === 'stoppage' ? AnalysisType.Cockpit : AnalysisType.Custom}
          />
        )}
        {step === NewAnalysisStep.addContributors && (
          <Card
            sx={{
              maxWidth: 800,
              minWidth: 600,
              minHeight: 250,
              padding: 4
            }}
          >
            {analysis && (
              <ManageContributors
                analysisId={analysis.id}
                canEdit={true}
                contributors={[]}
                owner={analysis.owner}
                onSucessCb={() => navigateToDetail(analysis.id)}
                skipAction={() => navigateToDetail(analysis.id)}
              />
            )}
          </Card>
        )}
      </Box>
    </PageContainer>
  )
}
